import React from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import type { Language } from '../../../i18n';
import { COLORS } from '../profileStyles';
import type { Translate } from '../profileTypes';

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
}

interface LanguageSelectModalProps {
  visible: boolean;
  language: Language;
  availableLanguages: LanguageOption[];
  onSelect: (code: Language) => void | Promise<void>;
  onClose: () => void;
  t: Translate;
}

export function LanguageSelectModal({
  visible,
  language,
  availableLanguages,
  onSelect,
  onClose,
  t,
}: LanguageSelectModalProps) {
  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View className="flex-1 bg-black/50 justify-center items-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <View className="flex-row items-center justify-between mb-2">
              <Text className="text-xl font-semibold text-foreground">{t('profile.selectLanguage')}</Text>
              <TouchableOpacity onPress={onClose}>
                <MaterialIcons name="close" size={24} color="#6b7280" />
              </TouchableOpacity>
            </View>
            <Text className="text-sm text-muted-foreground">{t('profile.selectLanguageDescription')}</Text>
          </CardHeader>
          <CardContent>
            <View className="gap-3">
              {availableLanguages.map((lang) => (
                <TouchableOpacity
                  key={lang.code}
                  onPress={() => onSelect(lang.code)}
                  className="border border-gray-300 rounded-lg p-4 bg-white"
                  style={{
                    borderColor: language === lang.code ? COLORS.primary : '#d1d5db',
                    backgroundColor: language === lang.code ? 'rgba(30, 64, 175, 0.05)' : 'white',
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`Select ${lang.nativeName}`}
                >
                  <View className="flex-row items-center justify-between">
                    <View>
                      <Text className="text-base font-semibold text-foreground">{lang.nativeName}</Text>
                      <Text className="text-sm text-muted-foreground">{lang.name}</Text>
                    </View>
                    {language === lang.code && (
                      <MaterialIcons name="check-circle" size={24} color={COLORS.primary} />
                    )}
                  </View>
                </TouchableOpacity>
              ))}
            </View>

            <Button
              variant="secondary"
              size="default"
              onPress={onClose}
              className="mt-4"
            >
              <Text className="font-medium">{t('common.cancel')}</Text>
            </Button>
          </CardContent>
        </Card>
      </View>
    </Modal>
  );
}
